import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { PERFIL_ROTULO, PIN_MAXIMO, PIN_MINIMO, SENHA_MINIMA } from "@caixaflow/shared";
import { api, ErroApi } from "../api/cliente";
import { useDefinirSessao, useSessao } from "../api/sessao";
import type { Sessao, UsuarioSessao } from "../api/tipos";
import { Campo } from "../components/Campos";
import { Carregando, mensagemDeErro } from "../components/Estados";
import { useToast } from "../components/Toast";

/** Dados de quem está logado, troca de senha e, para ADMIN, o PIN de autorização. */
export function PaginaPerfil() {
  const { data: sessao } = useSessao();
  if (!sessao) return <Carregando />;
  const u = sessao.usuario;
  return (
    <>
      <section className="card pad">
        <h2>Seus dados</h2>
        <dl className="dados">
          <dt>Nome</dt>
          <dd>{u.nome}</dd>
          <dt>E-mail</dt>
          <dd>{u.email}</dd>
          <dt>Perfil</dt>
          <dd>{PERFIL_ROTULO[u.perfil]}</dd>
        </dl>
      </section>
      {u.perfil === "ADMIN" && <SecaoPin sessao={sessao} />}
      <SecaoSenha />
    </>
  );
}

const apenasDigitos = (v: string) => v.replace(/\D/g, "").slice(0, PIN_MAXIMO);

function SecaoPin({ sessao }: { sessao: Sessao }) {
  const definirSessao = useDefinirSessao();
  const toast = useToast();
  const [editando, setEditando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [pin, setPin] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [senhaAtual, setSenhaAtual] = useState("");
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const temPin = sessao.usuario.temPin;

  const atualizarSessao = (usuario: UsuarioSessao) => definirSessao({ ...sessao, usuario: { ...sessao.usuario, temPin: usuario.temPin } });
  const reiniciar = () => {
    setEditando(false);
    setRemovendo(false);
    setPin("");
    setConfirmacao("");
    setSenhaAtual("");
    setErroLocal(null);
  };

  const salvar = useMutation({
    mutationFn: () => api<{ usuario: UsuarioSessao }>("/auth/pin", "POST", { pin, senhaAtual }),
    onSuccess: ({ usuario }) => {
      atualizarSessao(usuario);
      toast(temPin ? "PIN trocado" : "PIN cadastrado");
      reiniciar();
    },
    onError: () => setSenhaAtual(""),
  });
  const remover = useMutation({
    mutationFn: () => api<{ usuario: UsuarioSessao }>("/auth/pin/remover", "POST", { senhaAtual }),
    onSuccess: ({ usuario }) => {
      atualizarSessao(usuario);
      toast("PIN removido", "info");
      reiniciar();
    },
    onError: () => setSenhaAtual(""),
  });
  const erro = salvar.error instanceof ErroApi ? salvar.error : null;

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (pin !== confirmacao) {
      setErroLocal("A confirmação não confere com o PIN.");
      return;
    }
    setErroLocal(null);
    salvar.mutate();
  };

  return (
    <section className="card pad form">
      <div className="linha">
        <h2 style={{ margin: 0 }}>PIN de autorização</h2>
        <span className={`chip ${temPin ? "ok" : "neutro"}`}>{temPin ? "Cadastrado" : "Não cadastrado"}</span>
      </div>
      <p className="mudo pequeno" style={{ margin: 0 }}>
        Com o PIN, você autoriza no celular do vendedor cancelamentos, ajustes de venda e ofertas sem digitar e-mail e senha.
        Use de {PIN_MINIMO} a {PIN_MAXIMO} números, não compartilhe e troque depois do evento se alguém tiver visto.
      </p>

      {!editando && !removendo && (
        <div className="linha-esq">
          <button className="btn primario" onClick={() => setEditando(true)}>
            {temPin ? "Trocar PIN" : "Cadastrar PIN"}
          </button>
          {temPin && (
            <button className="btn fantasma" onClick={() => setRemovendo(true)}>
              Remover PIN
            </button>
          )}
        </div>
      )}

      {editando && (
        <form className="form" onSubmit={enviar} autoComplete="off">
          {(erroLocal || (salvar.error && !erro?.campos.length)) && (
            <div className="aviso erro" role="alert">
              {erroLocal ?? mensagemDeErro(salvar.error)}
            </div>
          )}
          <div className="duas-colunas">
            <Campo rotulo="Novo PIN" dica={`${PIN_MINIMO} a ${PIN_MAXIMO} números`} erro={erro?.doCampo("pin")}>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                className="campo-pin"
                required
                minLength={PIN_MINIMO}
                maxLength={PIN_MAXIMO}
                autoComplete="off"
                value={pin}
                onChange={(e) => setPin(apenasDigitos(e.target.value))}
              />
            </Campo>
            <Campo rotulo="Repita o PIN">
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                className="campo-pin"
                required
                maxLength={PIN_MAXIMO}
                autoComplete="off"
                value={confirmacao}
                onChange={(e) => setConfirmacao(apenasDigitos(e.target.value))}
              />
            </Campo>
          </div>
          <Campo rotulo="Sua senha" dica="para confirmar" erro={erro?.doCampo("senhaAtual")}>
            <input type="password" required autoComplete="current-password" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} />
          </Campo>
          <div className="modal-acoes">
            <button type="button" className="btn fantasma" onClick={reiniciar}>
              Cancelar
            </button>
            <button type="submit" className="btn primario" disabled={salvar.isPending}>
              {salvar.isPending ? "Salvando…" : "Salvar PIN"}
            </button>
          </div>
        </form>
      )}

      {removendo && (
        <form
          className="form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            remover.mutate();
          }}
        >
          {remover.error && (
            <div className="aviso erro" role="alert">
              {mensagemDeErro(remover.error)}
            </div>
          )}
          <p className="pequeno" style={{ margin: 0 }}>
            Sem PIN, as autorizações no celular do vendedor voltam a pedir o seu e-mail e senha.
          </p>
          <Campo rotulo="Sua senha" dica="para confirmar">
            <input type="password" required autoComplete="current-password" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} />
          </Campo>
          <div className="modal-acoes">
            <button type="button" className="btn fantasma" onClick={reiniciar}>
              Cancelar
            </button>
            <button type="submit" className="btn perigo" disabled={remover.isPending}>
              {remover.isPending ? "Removendo…" : "Remover PIN"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function SecaoSenha() {
  const definirSessao = useDefinirSessao();
  const toast = useToast();
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const trocar = useMutation({
    mutationFn: () => api<Sessao>("/auth/trocar-senha", "POST", { senhaAtual, novaSenha }),
    onSuccess: (nova) => {
      definirSessao(nova);
      toast("Senha trocada. As outras sessões abertas com a senha antiga foram encerradas.");
      setSenhaAtual("");
      setNovaSenha("");
      setConfirmacao("");
    },
  });
  const erro = trocar.error instanceof ErroApi ? trocar.error : null;

  return (
    <form
      className="card pad form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (novaSenha !== confirmacao) {
          setErroLocal("A confirmação não confere com a nova senha.");
          return;
        }
        setErroLocal(null);
        trocar.mutate();
      }}
    >
      <h2 style={{ margin: 0 }}>Trocar senha</h2>
      {(erroLocal || (trocar.error && !erro?.campos.length)) && (
        <div className="aviso erro" role="alert">
          {erroLocal ?? mensagemDeErro(trocar.error)}
        </div>
      )}
      <Campo rotulo="Senha atual" erro={erro?.doCampo("senhaAtual")}>
        <input type="password" autoComplete="current-password" required value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} />
      </Campo>
      <div className="duas-colunas">
        <Campo rotulo="Nova senha" dica={`mínimo ${SENHA_MINIMA} caracteres`} erro={erro?.doCampo("novaSenha")}>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={SENHA_MINIMA}
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
          />
        </Campo>
        <Campo rotulo="Repita a nova senha">
          <input type="password" autoComplete="new-password" required value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} />
        </Campo>
      </div>
      <div className="linha-esq">
        <button type="submit" className="btn primario" disabled={trocar.isPending}>
          {trocar.isPending ? "Salvando…" : "Trocar senha"}
        </button>
      </div>
    </form>
  );
}
